import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allArticles } from '@/mocks/data';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    window.location.href = '/';
    return null;
  }

  const relatedArticles = allArticles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishDate,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: 'Serenity',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Serenity',
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: article.category },
              { name: article.title },
            ]}
          />
        </div>

        {/* Article Header */}
        <section className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs bg-primary-100 text-primary-700 px-3 py-1 rounded-full font-medium">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">{article.publishDate}</span>
            <span className="text-xs text-foreground-500">· 阅读 {article.readTime}</span>
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
            {article.excerpt}
          </p>
        </section>

        {/* Featured Image */}
        <section className="w-full max-w-4xl mx-auto px-4 md:px-6 mb-10">
          <div className="aspect-[16/9] rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full max-w-3xl mx-auto px-4 md:px-6 pb-14">
          <article
            className="prose-custom text-foreground-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-background-200/70">
            {article.tags.map((tag, i) => (
              <span
                key={i}
                className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        title={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-foreground-500">{item.publishDate} · {item.readTime}</span>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mt-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}